import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {Line, Bar} from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {RootState} from "@/store";
import ReportResultLayout from "@/pages/Report/ReportResultLayout";
import ReportSummaryCard from "@/pages/Report/ReportSummaryCard";
import {currencyFormat, hourFormat} from "@/utils";
import BookingInsightTable from "@/pages/Report/BookingInsightTable";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const hoursByDayOptions = {
  responsive: true,
  plugins: {
    legend: null
  }
}

const bookingByHoursOptions = {
  responsive: true,
  plugins: {
    legend: null
  }
}

const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const BookingTimesResult = () => {
  const bookingTimes = useSelector((state: RootState) => state.bookingInsight.state.bookingTimes)
  const [weekDayBookingData, setWeekDayBookingData] = useState([])
  const [hourBookingData, setHourBookingData] = useState<BookingByHourItem[]>([])

  useEffect(() => {
    const weekDaysBookings = bookingTimes?.booking_by_week_day.list ?? []
    setWeekDayBookingData(weekDays?.map((l) => weekDaysBookings.find(d => d.day === l)?.total_booking) ?? [])

    const hourBookings = bookingTimes?.booking_by_hour.list ?? []
    setHourBookingData(hourBookings)
  }, [bookingTimes])

  const weekDaysData = {
    labels: weekDays.map(day => day.slice(0, 3)),
    datasets: [
      {
        label: 'Booking Hours by Day',
        data: weekDayBookingData,
        borderColor: 'rgb(53, 162, 235)',
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
      },
    ],
  };

  const hoursData = {
    labels: hourBookingData.map((d) => `${hourFormat(d.start_time)} - ${hourFormat(d.end_time)}`),
    datasets: [
      {
        label: 'Booking by Hour',
        data: hourBookingData.map((d) => d.total_booking),
        borderColor: '#009BDD',
        backgroundColor: '#009BDD',
      },
    ],
  };

  const weekDaysTableData = () => {
    const weekDaysBookings = bookingTimes?.booking_by_week_day.list ?? []
    return weekDays.map((w) => {
      const found = weekDaysBookings.find(d => d.day === w)
      const totalBooking = found?.total_booking ?? 0
      const totalRevenue = found?.total_revenue ?? 0
      return [w, totalBooking, currencyFormat(totalRevenue)]
    })
  }

  const hourTableData = hourBookingData.map((h) => {
    const time = `${hourFormat(h.start_time)} - ${hourFormat(h.end_time)}`
    return [time, h.total_booking, currencyFormat(h.total_revenue)]
  })

  return (
    <>
      {
        bookingTimes &&
        <ReportResultLayout
          useHeaderVenue
          useHeaderDate
          overrideHeader="Booking Times"
          reportType='BOOKING_INSIGHT'
					selectedInsightType='BOOKING_METHOD'>
          {
            bookingTimes &&
            <div className="j-col gap-7">
              <div className="text-jxl">
                Booking Hours by Day
              </div>
              {
                weekDayBookingData.length &&
                <Line options={hoursByDayOptions} data={weekDaysData} />
              }
              <div className="flex flex-row w-full gap-7">
                <div className="flex flex-col gap-6">
                  <ReportSummaryCard
                    isPlainText
                    title="Total Booking"
                    total={bookingTimes?.booking_by_week_day?.total_booking ?? 0}
                    classNames="border-l-green-success border-l-[3px]"/>
                  <ReportSummaryCard
                    title="Total Values"
                    total={bookingTimes?.booking_by_week_day?.total_revenue ?? 0}
                    classNames="border-l-green-success border-l-[3px]"/>
                </div>
                <div className="flex flex-grow w-full">
                  <BookingInsightTable
                    headers={['DAYS', 'BOOKINGS', 'VALUES']}
                    data={weekDaysTableData()}
                  />
                </div>
              </div>
              <div className="horizontal-separator"/>
              <div className="text-jxl">
                Booking by Hours
              </div>
              {
                hourBookingData.length &&
                <Bar options={bookingByHoursOptions} data={hoursData} />
              }
              <div className="flex flex-row w-full gap-7">
                <div className="flex flex-col gap-6">
                  <ReportSummaryCard
                    isPlainText
                    title="Total Booking"
                    total={bookingTimes?.booking_by_hour?.total_booking ?? 0}
                    classNames="border-l-green-success border-l-[3px]"/>
                  <ReportSummaryCard
                    title="Total Values"
                    total={bookingTimes?.booking_by_hour  ?.total_revenue ?? 0}
                    classNames="border-l-green-success border-l-[3px]"/>
                </div>
                <div className="flex flex-grow w-full">
                  <BookingInsightTable
                    headers={['HOURS', 'TOTAL BOOKINGS', 'VALUES']}
                    data={hourTableData}
                  />
                </div>
              </div>
            </div>
          }
        </ReportResultLayout>
      }
    </>
  )

}

export default BookingTimesResult;